import { OrderCheckoutState, OrderPriceState } from '@shared/types';
import { FunctionComponent, ReactNode, useMemo } from 'react';
import styles from '../BookingCheckoutSummary/styles.module.scss';
import { CheckoutSummary, CheckoutSummaryProps } from '..';
import { useCostSummary } from '../../data/useCostSummary';
import { PriceSummary } from '../BookingCheckoutSummary/PriceSummary';
import { IconSummary } from '../IconSummary';
import { LaptopLightIcon } from '../../../Icons';
import { useCheckoutContext } from '../../CheckoutContext';
import { PublicOnDemandCourse } from '@cademy-package/sdk-component-types';

export type OrderCheckoutSummaryProps = {
    course: PublicOnDemandCourse;
    checkoutState: OrderCheckoutState;
};

export const OrderCheckoutSummary: FunctionComponent<OrderCheckoutSummaryProps> = ({
    course,
    checkoutState,
}) => {
    const { educator } = useCheckoutContext();
    const hidePricing = useMemo(
        () => checkoutState.origin !== 'marketplace' && educator.feature_flags?.hide_pricing,
        [checkoutState.origin, educator.feature_flags]
    );
    const { offerId: selectedOffer, voucherId } = useMemo((): OrderPriceState => {
        if (checkoutState.stepState.price.status === 'complete') {
            return checkoutState.stepState.price.state;
        }
        return { offerId: '' };
    }, [checkoutState]);

    const selectedOffers = useMemo(() => {
        if (!selectedOffer) {
            [];
        }
        return [
            {
                offerId: selectedOffer,
                quantity: 1,
            },
        ];
    }, [selectedOffer]);

    const { data: costSummary, isPreviousData: isLoadingCostSummary } = useCostSummary(
        {
            courseId: course._id,
            addons: [],
            offers: selectedOffers,
            origin: checkoutState.origin,
            paymentType: checkoutState.stepState.payment.state?.paymentMethod,
            voucherId: voucherId,
        },
        {
            keepPreviousData: true,
        }
    );

    const detailsSection = useMemo((): ReactNode => {
        return (
            <>
                <IconSummary Icon={LaptopLightIcon}>
                    On-demand online {course.keyword || 'course'}
                </IconSummary>
            </>
        );
    }, [course]);

    const offersSection = useMemo((): ReactNode => {
        if (!costSummary || hidePricing) {
            return null;
        }
        const { offerSummary, addOnSummary } = costSummary.summary_items;
        if (!offerSummary.length && !addOnSummary.length) {
            return null;
        }
        return (
            <ul className={styles.costSection}>
                {offerSummary.map((offer, index) => {
                    return (
                        <li key={index}>
                            <PriceSummary
                                label={offer.text}
                                price={offer.value}
                                currency={course.currency}
                            />
                        </li>
                    );
                })}
                {addOnSummary.map((addon, index) => {
                    return (
                        <li key={index}>
                            <PriceSummary
                                label={addon.text}
                                price={addon.value}
                                currency={course.currency}
                            />
                        </li>
                    );
                })}
            </ul>
        );
    }, [costSummary, course.currency, hidePricing]);

    const discountsAndVouchersSection = useMemo((): ReactNode => {
        if (!costSummary || hidePricing) {
            return null;
        }
        const { discountSummary, voucherSummary } = costSummary.summary_items;
        if (!discountSummary.length && !voucherSummary.length) {
            return null;
        }
        return (
            <ul className={styles.costSection}>
                {discountSummary.map((discount, index) => {
                    return (
                        <li key={index}>
                            <PriceSummary
                                label={discount.text}
                                price={-discount.value}
                                currency={course.currency}
                            />
                        </li>
                    );
                })}
                {voucherSummary.map((voucher, index) => {
                    return (
                        <li key={index}>
                            <PriceSummary
                                label={voucher.text}
                                price={-voucher.value}
                                currency={course.currency}
                            />
                        </li>
                    );
                })}
            </ul>
        );
    }, [costSummary, course.currency, hidePricing]);

    const additionalChargesSection = useMemo((): ReactNode => {
        if (!costSummary) {
            return null;
        }
        const { vatSummary } = costSummary.summary_items;
        const { cademy_fees, fees_absorbed } = costSummary;
        if (!vatSummary.length && (!cademy_fees || fees_absorbed === true)) {
            return null;
        }
        return (
            <ul className={styles.costSection}>
                {vatSummary.map((vat, index) => {
                    return (
                        <li key={index}>
                            <PriceSummary
                                label={vat.text}
                                price={vat.value}
                                currency={course.currency}
                            />
                        </li>
                    );
                })}
                {cademy_fees && fees_absorbed !== true ? (
                    <li>
                        <PriceSummary label="Fees" price={cademy_fees} currency={course.currency} />
                    </li>
                ) : null}
            </ul>
        );
    }, [costSummary, course.currency]);

    const totalSection = useMemo((): ReactNode => {
        if (!costSummary || hidePricing) {
            return null;
        }
        return (
            <PriceSummary
                className={styles.totalSummary}
                label="Total"
                price={costSummary.total_cost}
                currency={course.currency}
            />
        );
    }, [costSummary, course.currency, hidePricing]);

    const sections = useMemo((): CheckoutSummaryProps['summarySections'] => {
        return [
            detailsSection,
            offersSection,
            discountsAndVouchersSection,
            additionalChargesSection,
            totalSection,
        ];
    }, [
        detailsSection,
        offersSection,
        discountsAndVouchersSection,
        additionalChargesSection,
        totalSection,
    ]);

    return (
        <CheckoutSummary
            course={course}
            taglineDetail={null}
            summarySections={sections}
            totalCost={costSummary?.total_cost}
        />
    );
};
